import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Minus,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
  Wand2,
} from 'lucide-react';
import { addDays, format, startOfWeek } from 'date-fns';
import { NavLink } from 'react-router-dom';
import type { CapacityAnalysis, ScheduleBlock, TaskUI } from '@flexiplanner/types';
import { numberToPriority, taskToUI } from '@flexiplanner/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { getCachedCalendarEvents, setCachedCalendarEvents } from '@/lib/calendarCache';
import { cn } from '@/lib/utils';
import { useScheduleStore } from '@/stores/scheduleStore';
import type { CalendarEvent } from '@/stores/scheduleStore';
import { useTasksStore } from '@/stores/tasksStore';
import { useUserStore } from '@/stores/userStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { usePinchZoom } from '@/hooks/usePinchZoom';
import { DroppableTimeSlot } from './DroppableTimeSlot';
import { ScheduledTaskBlock } from './ScheduledTaskBlock';
import { CalendarCommitmentEditor } from './CalendarCommitmentEditor';
import { CalendarEmptyState } from './CalendarEmptyState';
import { TaskEditor } from '@/components/blocks/TaskEditor';

const HOURS = Array.from({ length: 24 }, (_, index) => index);

function toLocalDateString(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`;
}

function asWeekStart(date: Date) {
  return startOfWeek(date, { weekStartsOn: 1 });
}

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function densitySlotHeight(density: 'compact' | 'balanced' | 'spacious') {
  if (density === 'compact') return 48;
  if (density === 'spacious') return 78;
  return 60;
}

function getDropMinute(event: DragEndEvent, fallbackMinute: number, slotHeight: number) {
  const translated = event.active.rect.current.translated;
  const initial = event.active.rect.current.initial;
  const activeRect = translated || initial;
  const overRect = event.over?.rect;
  if (!activeRect || !overRect) return fallbackMinute;

  const pointerY = activeRect.top + activeRect.height / 2;
  const relative = Math.max(0, Math.min(slotHeight, pointerY - overRect.top));
  return Math.max(0, Math.min(59, Math.round((relative / slotHeight) * 60)));
}

export function WeekView() {
  const { toast } = useToast();
  const [isScheduling, setIsScheduling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncingCalendar, setIsSyncingCalendar] = useState(false);
  const [capacity, setCapacity] = useState<CapacityAnalysis | null>(null);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [constraintOverride, setConstraintOverride] = useState(false);
  const [slotHeight, setSlotHeight] = useState(60);
  const [solverStatus, setSolverStatus] = useState('Ready to solve open activities.');
  const [editingCalendarEvent, setEditingCalendarEvent] = useState<CalendarEvent | null>(null);
  const [editingTask, setEditingTask] = useState<TaskUI | null>(null);
  const autoSyncedWeeksRef = useRef<Set<string>>(new Set());
  const isAutoSyncingRef = useRef(false);

  const currentDate = useScheduleStore(s => s.currentDate);
  const setCurrentDate = useScheduleStore(s => s.setCurrentDate);
  const setView = useScheduleStore(s => s.setView);
  const setBlocks = useScheduleStore(s => s.setBlocks);
  const setGhostBlocks = useScheduleStore(s => s.setGhostBlocks);
  const setCalendarEvents = useScheduleStore(s => s.setCalendarEvents);
  const blocks = useScheduleStore(s => s.blocks);
  const calendarEvents = useScheduleStore(s => s.calendarEvents);
  const ghostBlocks = useScheduleStore(s => s.ghostBlocks);
  const loadWorkspace = useWorkspaceStore(s => s.loadWorkspace);

  const tasks = useTasksStore(s => s.tasks);
  const setTasks = useTasksStore(s => s.setTasks);
  const updateTask = useTasksStore(s => s.updateTask);
  const deleteTask = useTasksStore(s => s.deleteTask);
  const workHours = useUserStore(s => s.settings.workHours);
  const calendarDensity = useUserStore(s => s.settings.calendarDensity);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 180, tolerance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 220, tolerance: 10 },
    })
  );

  const weekStart = useMemo(() => asWeekStart(currentDate), [currentDate]);
  const weekStartKey = toLocalDateString(weekStart);
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart]
  );
  const workingWindow = useMemo(() => parseWorkingWindow(workHours), [workHours]);

  useEffect(() => {
    setView('week');
  }, [setView]);

  useEffect(() => {
    setSlotHeight(densitySlotHeight(calendarDensity));
  }, [calendarDensity]);

  useEffect(() => {
    let cancelled = false;

    async function loadWeek() {
      void loadWorkspace();
      await refreshScheduler();
      const connected = await refreshCalendarStatus();

      if (
        cancelled ||
        !connected ||
        autoSyncedWeeksRef.current.has(weekStartKey) ||
        isAutoSyncingRef.current
      ) {
        return;
      }

      isAutoSyncingRef.current = true;
      setIsSyncingCalendar(true);
      setSolverStatus('Syncing calendar constraints in the background...');

      try {
        const response = await api.calendar.sync();
        if (!cancelled && response.success) {
          await refreshScheduler();
          autoSyncedWeeksRef.current.add(weekStartKey);
          const warningCount = Array.isArray(response.data?.warnings) ? response.data.warnings.length : 0;
          setSolverStatus(
            warningCount > 0
              ? `Calendar constraints partially synced. ${warningCount} calendar${warningCount === 1 ? '' : 's'} need attention.`
              : 'Calendar constraints are current. Ready to solve open activities.'
          );
        } else if (!response.success) {
          if (!cancelled) {
            setSolverStatus(
              `Calendar sync needs attention: ${response.error?.message || 'Reload calendars or reconnect Google Calendar.'}`
            );
          }
          console.warn('Background calendar sync failed', response.error);
        }
      } catch (error) {
        if (!cancelled) {
          setSolverStatus('Calendar sync needs attention. Reload calendars or reconnect Google Calendar.');
        }
        console.warn('Background calendar sync failed', error);
      } finally {
        isAutoSyncingRef.current = false;
        if (!cancelled) setIsSyncingCalendar(false);
      }
    }

    void loadWeek();

    return () => {
      cancelled = true;
    };
  }, [weekStartKey, loadWorkspace]);

  const scheduledTaskBlocks = useMemo<ScheduleBlock[]>(() => {
    return tasks
      .filter(task => task.scheduledStart && task.scheduledEnd)
      .map(task => ({
        id: `task-${task.id}`,
        taskId: task.id,
        title: task.title,
        startTime: task.scheduledStart!,
        endTime: task.scheduledEnd!,
        isGhost: false,
        priority: task.priority,
        requiredEnergy: task.energyLevel,
        stateProfile: task.stateProfile,
        customAttributes: task.customAttributes,
      }));
  }, [tasks]);

  const calendarBlocks = useMemo(() => {
    return calendarEvents.map(event => ({
      id: `gcal-${event.id}`,
      taskId: event.id,
      title: event.title,
      startTime: event.start,
      endTime: event.end,
      isGhost: false,
      isCalendarEvent: true,
      isFreeTimeHold: event.isFlexHold,
      priority: 4,
      stateProfile: event.stateProfile,
      customAttributes: event.customAttributes,
      requiredEnergy: event.stateProfile?.required_productivity_energy ?? undefined,
    }));
  }, [calendarEvents]);

  const displayBlocks = useMemo(() => {
    const merged = new Map<string, ScheduleBlock & { isCalendarEvent?: boolean }>();
    const taskIds = new Set(tasks.map(task => task.id));
    [...blocks, ...scheduledTaskBlocks, ...calendarBlocks].forEach(block => {
      if (block.isGhost && block.taskId && !taskIds.has(block.taskId)) return;
      merged.set(block.id, block);
    });
    return Array.from(merged.values());
  }, [blocks, scheduledTaskBlocks, calendarBlocks, tasks]);

  const taskById = useMemo(() => new Map(tasks.map(task => [task.id, task])), [tasks]);
  const calendarEventById = useMemo(
    () => new Map(calendarEvents.map(event => [event.id, event])),
    [calendarEvents]
  );

  const blocksBySlot = useMemo(() => {
    const grouped = new Map<string, ScheduleBlock[]>();
    for (const block of displayBlocks) {
      const start = new Date(block.startTime);
      const key = `${toLocalDateString(start)}-${start.getHours()}`;
      const existing = grouped.get(key);
      if (existing) existing.push(block);
      else grouped.set(key, [block]);
    }
    return grouped;
  }, [displayBlocks]);

  const unscheduled = useMemo(
    () =>
      tasks.filter(
        task =>
          !task.scheduledStart &&
          !task.scheduledEnd &&
          task.status !== 'done' &&
          task.status !== 'completed' &&
          task.status !== 'cancelled'
      ),
    [tasks]
  );
  const needsDetails = useMemo(
    () =>
      unscheduled.filter(task => getMissingPlanningFields(task).length > 0),
    [unscheduled]
  );
  const scheduledHours = useMemo(
    () =>
      tasks.reduce((sum, task) => {
        if (!task.scheduledStart || !task.scheduledEnd) return sum;
        return (
          sum +
          (new Date(task.scheduledEnd).getTime() - new Date(task.scheduledStart).getTime()) / 3600000
        );
      }, 0),
    [tasks]
  );
  const hasCalendarContent = displayBlocks.length > 0 || isRefreshing;
  const pinchHandlers = usePinchZoom({
    setValue: setSlotHeight,
    min: 36,
    max: 156,
  });

  async function refreshTasks(): Promise<TaskUI[]> {
    const response = await api.tasks.list();
    if (response.success && response.data) {
      const tasksList = Array.isArray(response.data) ? response.data : (response.data as any).tasks;
      if (tasksList) {
        const nextTasks = tasksList.map(taskToUI);
        setTasks(nextTasks);
        return nextTasks;
      }
    }
    return useTasksStore.getState().tasks;
  }

  async function refreshScheduler() {
    setIsRefreshing(true);
    try {
      const rolloverResponse = await api.schedule.rollover();
      if (rolloverResponse.success && rolloverResponse.data?.rolledOver > 0) {
        const { rolledOver, rescheduled, failed } = rolloverResponse.data;
        setSolverStatus(
          `Replanned ${rolledOver} expired activit${rolledOver === 1 ? 'y' : 'ies'}: ${rescheduled} placed, ${failed} still blocked.`
        );
      }

      const calendarStart = weekStart.toISOString();
      const calendarEnd = addDays(weekStart, 7).toISOString();

      const cachedCalendarEvents = getCachedCalendarEvents(calendarStart, calendarEnd);
      if (cachedCalendarEvents) {
        setCalendarEvents(cachedCalendarEvents);
        setSolverStatus('Showing cached Calendar constraints. Refreshing Google Calendar in the background...');
      }

      const calendarPromise = api.calendar.getEvents(calendarStart, calendarEnd)
        .then(calendarResponse => {
          if (calendarResponse.success && calendarResponse.data) {
            const normalizedEvents = normalizeCalendarEvents(calendarResponse.data);
            setCalendarEvents(normalizedEvents);
            setCachedCalendarEvents(calendarStart, calendarEnd, normalizedEvents);
            setSolverStatus(current =>
              current.startsWith('Replanned ')
                ? current
                : 'Calendar constraints are current. Ready to solve open activities.'
            );
          }
        })
        .catch(error => {
          console.warn('Background Google Calendar refresh failed', error);
        });

      const [tasksResponse, ghostResponse, capacityResponse, overrideResponse] = await Promise.all([
        api.tasks.list(),
        api.schedule.getGhostBlocks(weekStartKey),
        api.schedule.checkFeasibility(weekStartKey),
        api.schedule.getConstraintOverride(weekStartKey),
      ]);

      let freshTaskIds = new Set(useTasksStore.getState().tasks.map(task => task.id));
      if (tasksResponse.success && tasksResponse.data) {
        const tasksList = Array.isArray(tasksResponse.data)
          ? tasksResponse.data
          : (tasksResponse.data as any).tasks;
        if (tasksList) {
          const nextTasks = tasksList.map(taskToUI);
          setTasks(nextTasks);
          freshTaskIds = new Set(nextTasks.map((task: TaskUI) => task.id));
        }
      }

      if (ghostResponse.success && ghostResponse.data) {
        setBlocks(normalizeScheduleBlocks(ghostResponse.data, freshTaskIds));
        setGhostBlocks(normalizeGhostBlocks(ghostResponse.data, freshTaskIds));
        if (!cachedCalendarEvents && ghostResponse.data.gcal_events?.length) {
          const normalizedEvents = normalizeCalendarEvents(ghostResponse.data.gcal_events || []);
          setCalendarEvents(normalizedEvents);
          setCachedCalendarEvents(calendarStart, calendarEnd, normalizedEvents);
        }
      }

      if (capacityResponse.success && capacityResponse.data) {
        setCapacity(capacityResponse.data);
      }

      if (overrideResponse.success && overrideResponse.data) {
        setConstraintOverride(Boolean(overrideResponse.data.constraint_override_enabled));
      }

      void calendarPromise;
    } finally {
      setIsRefreshing(false);
    }
  }

  async function refreshCalendarStatus(): Promise<boolean> {
    const response = await api.calendar.getStatus();
    if (response.success && response.data) {
      const connected = Boolean(response.data.connected);
      setCalendarConnected(connected);
      return connected;
    }
    setCalendarConnected(false);
    return false;
  }

  async function runAutoSchedule() {
    setIsScheduling(true);
    setSolverStatus('Starting background solve...');
    try {
      const response = await api.schedule.autoSchedule(weekStartKey);
      if (!response.success) throw new Error(response.error?.message || 'Auto-schedule failed');

      let status = response.data;
      setSolverStatus(status?.message || 'Solve queued in the background.');
      toast({
        title: 'Background solve started',
        description: status?.total
          ? `${status.total} activities queued. You can keep using Fluxiplanner.`
          : 'Fluxiplanner will keep solving in the background.',
      });

      for (let attempt = 0; attempt < 180; attempt++) {
        if (status?.status === 'completed' || status?.status === 'blocked' || status?.status === 'failed') {
          break;
        }
        await sleep(2000);
        const statusResponse = await api.schedule.autoScheduleStatus();
        if (!statusResponse.success) break;
        status = statusResponse.data;
        const placed = status?.scheduled ?? 0;
        const pending = status?.pending ?? 0;
        setSolverStatus(
          status?.message || `Background solve running: ${placed} placed, ${pending} remaining.`
        );
        if (placed > 0 && attempt % 3 === 2) {
          await refreshTasks();
          await refreshScheduler();
        }
      }

      await refreshTasks();
      await refreshScheduler();
      const scheduled = status?.scheduled ?? 0;
      const optimized = status?.optimized ?? 0;
      const failed = status?.failed ?? 0;
      const pending = status?.pending ?? 0;
      setSolverStatus(
        pending > 0
          ? `Background solve is still running: ${scheduled} placed, ${pending} queued.`
          : scheduled > 0
          ? `Placed ${scheduled} activit${scheduled === 1 ? 'y' : 'ies'} and re-optimized ${optimized} routine block${optimized === 1 ? '' : 's'}${failed ? `; ${failed} still open.` : '.'}`
          : failed
            ? `${failed} activit${failed === 1 ? 'y' : 'ies'} still need more room.`
            : 'No open activities needed placement.'
      );
      toast({
        title: pending ? 'Solve still running' : failed ? 'Schedule partially solved' : 'Schedule solved',
        description: `${scheduled} activities placed, ${pending} queued, ${failed} blocked.`,
        variant: scheduled === 0 && (failed > 0 || pending > 0) ? 'destructive' : 'default',
      });
    } catch (error) {
      setSolverStatus('Solve failed. Relax constraints or try Override.');
      toast({
        title: 'Schedule could not be solved',
        description: error instanceof Error ? error.message : 'Try relaxing constraints.',
        variant: 'destructive',
      });
    } finally {
      setIsScheduling(false);
    }
  }

  async function confirmGhostBlock(ghostBlockId: string) {
    const response = await api.schedule.confirmGhostBlock(ghostBlockId);
    if (!response.success) {
      toast({
        title: 'Suggestion expired',
        description: response.error?.message || 'Generate a fresh set of suggestions.',
        variant: 'destructive',
      });
      return;
    }
    await refreshTasks();
    await refreshScheduler();
    toast({ title: 'Suggestion confirmed', description: 'The activity has been pinned to the timeline.' });
  }

  async function syncCalendar() {
    setIsSyncingCalendar(true);
    try {
      const response = await api.calendar.sync();
      if (!response.success) throw new Error(response.error?.message || 'Calendar sync failed');
      await refreshScheduler();
      const warningCount = Array.isArray(response.data?.warnings) ? response.data.warnings.length : 0;
      toast({
        title: warningCount > 0 ? 'Calendar partially synced' : 'Calendar synced',
        description:
          warningCount > 0
            ? `${response.data?.synced ?? 0} events refreshed. ${warningCount} calendar${warningCount === 1 ? '' : 's'} need attention.`
            : `${response.data?.synced ?? 0} events refreshed.`,
      });
    } catch (error) {
      toast({
        title: calendarConnected ? 'Calendar sync failed' : 'Calendar not connected',
        description: error instanceof Error ? error.message : 'Connect Google Calendar in settings.',
        variant: 'destructive',
      });
    } finally {
      setIsSyncingCalendar(false);
    }
  }

  async function toggleOverride() {
    const next = !constraintOverride;
    setConstraintOverride(next);
    const response = await api.schedule.setConstraintOverride(weekStartKey, next);
    if (!response.success) {
      setConstraintOverride(!next);
      toast({
        title: 'Override not saved',
        description: response.error?.message || 'Try again.',
        variant: 'destructive',
      });
      return;
    }
    await refreshScheduler();
  }

  function handleCalendarEventSaved(updatedEvent: CalendarEvent) {
    setCalendarEvents(
      calendarEvents.map(event => (event.id === updatedEvent.id ? updatedEvent : event))
    );
    setEditingCalendarEvent(null);
    toast({
      title: 'Commitment updated',
      description: 'Energy and attribute metadata were saved for this calendar commitment.',
    });
    void refreshScheduler();
  }

  async function handleSaveTask(updates: Partial<TaskUI>) {
    if (!editingTask) return;

    try {
      const response = await api.tasks.update(editingTask.id, {
        title: updates.title,
        description: updates.description,
        bundle_id: updates.bundleId,
        due_date: updates.deadline,
        do_date: updates.scheduledStart,
        do_date_end: updates.scheduledEnd,
        duration_minutes: updates.estimatedDuration,
        priority: updates.priority ? numberToPriority(updates.priority) : undefined,
        state_profile: updates.stateProfile,
        custom_attributes: updates.customAttributes,
      });
      if (response.success && response.data) {
        updateTask(editingTask.id, taskToUI((response.data as any).task || response.data));
      } else {
        updateTask(editingTask.id, updates);
      }
    } catch (error) {
      console.error('Failed to save activity', error);
      updateTask(editingTask.id, updates);
    }
  }

  async function handleDeleteTask() {
    if (!editingTask) return;

    try {
      await api.tasks.delete(editingTask.id);
      deleteTask(editingTask.id);
      setEditingTask(null);
    } catch (error) {
      console.error('Failed to delete activity', error);
    }
  }

  async function handleRescheduleTomorrow() {
    if (!editingTask) return;

    const response = await api.schedule.scheduleTaskTomorrow(editingTask.id);
    if (!response.success) {
      toast({
        title: 'Could not move activity',
        description: response.error?.message || 'No optimal slot was found tomorrow.',
        variant: 'destructive',
      });
      return;
    }

    const taskDoc = response.data?.task || response.data?.task?.task;
    if (taskDoc) updateTask(editingTask.id, taskToUI(taskDoc));
    setEditingTask(null);
    await refreshTasks();
    await refreshScheduler();
    toast({
      title: 'Moved to tomorrow',
      description: response.data?.notification?.message || 'The scheduler picked the best available slot.',
    });
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    if (active.data.current?.type === 'ScheduledTask' && over.data.current?.type === 'TimeSlot') {
      const block = active.data.current.block as ScheduleBlock;
      if (block.isGhost || block.id.startsWith('gcal-')) return;

      const targetDateKey = over.data.current.dateKey;
      const targetHour = over.data.current.hour;
      const oldStart = new Date(block.startTime);
      const durationMs = new Date(block.endTime).getTime() - oldStart.getTime();
      const targetMinute = getDropMinute(event, oldStart.getMinutes(), slotHeight);
      const newStart = new Date(`${targetDateKey}T00:00:00`);
      newStart.setHours(targetHour, targetMinute, 0, 0);
      const newEnd = new Date(newStart.getTime() + durationMs);

      updateTask(block.taskId, {
        scheduledStart: newStart.toISOString(),
        scheduledEnd: newEnd.toISOString(),
      });

      const response = await api.tasks.update(block.taskId, {
        do_date: newStart.toISOString(),
        do_date_end: newEnd.toISOString(),
      });
      if (!response.success) {
        await refreshTasks();
        toast({
          title: 'Move failed',
          description: response.error?.message || 'The activity stayed in its previous slot.',
          variant: 'destructive',
        });
      } else {
        await refreshScheduler();
      }
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 sm:gap-4">
      <section className="planner-hero shrink-0 overflow-visible rounded-lg border border-white/10 text-white shadow-2xl sm:overflow-hidden">
        <div className="grid gap-2 p-2.5 sm:gap-4 sm:p-4 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-1.5 sm:mb-4 sm:gap-2">
              <Badge className="max-w-full border-cyan-300/30 bg-cyan-300/10 text-[10px] leading-none text-cyan-100 hover:bg-cyan-300/15 sm:text-xs">
                <Sparkles className="mr-1 h-3 w-3" />
                Solver week
              </Badge>
              <span className="text-[11px] leading-none text-white/60 sm:text-sm">
                {format(weekStart, 'MMM d')} - {format(addDays(weekStart, 6), 'MMM d, yyyy')}
              </span>
            </div>
            <h1 className="max-w-3xl text-base font-semibold leading-tight tracking-normal min-[380px]:text-lg sm:text-3xl lg:text-4xl">
              A live command board for time, energy, and immovable commitments.
            </h1>
            <div className="mt-3 grid grid-cols-3 gap-1.5 sm:mt-5 sm:gap-3">
              <Metric label="Open activities" value={String(unscheduled.length)} tone="cyan" />
              <Metric label="Placed hours" value={scheduledHours.toFixed(1)} tone="amber" />
              <Metric
                label="Capacity"
                value={capacity ? `${Math.round(capacity.utilization_percent)}%` : '--'}
                tone={capacity?.is_feasible === false ? 'rose' : 'green'}
              />
            </div>
          </div>

          <div className="min-w-0 rounded-lg border border-white/10 bg-white/[0.06] p-2 sm:p-3">
            <div className="mb-2 flex items-start justify-between gap-2 sm:mb-3">
              <div className="min-w-0">
                <p className="text-xs font-medium sm:text-sm">Scheduling systems</p>
                <p className="text-[11px] leading-tight text-white/55 sm:text-xs">Solver, suggestions, Calendar constraints</p>
              </div>
              {isRefreshing && <Loader2 className="h-4 w-4 animate-spin text-cyan-200" />}
            </div>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              <Button
                onClick={runAutoSchedule}
                disabled={isScheduling}
                size="sm"
                className="min-w-[7rem] flex-1 basis-[calc(50%-0.1875rem)] bg-cyan-300 px-2 text-xs text-slate-950 hover:bg-cyan-200 sm:basis-[calc(50%-0.25rem)] sm:text-sm"
              >
                {isScheduling ? <Loader2 className="mr-1.5 h-3.5 w-3.5 shrink-0 animate-spin sm:mr-2 sm:h-4 sm:w-4" /> : <Wand2 className="mr-1.5 h-3.5 w-3.5 shrink-0 sm:mr-2 sm:h-4 sm:w-4" />}
                Solve
              </Button>
              <Button
                variant="outline"
                onClick={() => void refreshScheduler()}
                size="sm"
                className="min-w-[7rem] flex-1 basis-[calc(50%-0.1875rem)] border-white/15 bg-white/5 px-2 text-xs text-white hover:bg-white/10 sm:basis-[calc(50%-0.25rem)] sm:text-sm"
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5 shrink-0 sm:mr-2 sm:h-4 sm:w-4" />
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={syncCalendar}
                disabled={isSyncingCalendar}
                size="sm"
                className="min-w-[7rem] flex-1 basis-[calc(50%-0.1875rem)] border-white/15 bg-white/5 px-2 text-xs text-white hover:bg-white/10 sm:basis-[calc(50%-0.25rem)] sm:text-sm"
              >
                {isSyncingCalendar ? <Loader2 className="mr-1.5 h-3.5 w-3.5 shrink-0 animate-spin sm:mr-2 sm:h-4 sm:w-4" /> : <CalendarClock className="mr-1.5 h-3.5 w-3.5 shrink-0 sm:mr-2 sm:h-4 sm:w-4" />}
                Sync
              </Button>
              <Button
                variant="outline"
                onClick={toggleOverride}
                size="sm"
                className={cn(
                  'min-w-[7rem] flex-1 basis-[calc(50%-0.1875rem)] border-white/15 bg-white/5 px-2 text-xs text-white hover:bg-white/10 sm:basis-[calc(50%-0.25rem)] sm:text-sm',
                  constraintOverride && 'border-amber-300/50 bg-amber-300/15 text-amber-100'
                )}
              >
                {constraintOverride ? <Check className="mr-1.5 h-3.5 w-3.5 shrink-0 sm:mr-2 sm:h-4 sm:w-4" /> : <AlertTriangle className="mr-1.5 h-3.5 w-3.5 shrink-0 sm:mr-2 sm:h-4 sm:w-4" />}
                Override
              </Button>
            </div>
            <div className="mt-2 min-w-0 rounded-md border border-white/10 bg-black/20 p-2 text-[11px] leading-snug text-white/65 sm:mt-3 sm:p-3 sm:text-xs">
              <div className="break-words">
                {capacity?.is_feasible === false
                  ? `${capacity.overflow_hours?.toFixed(1) ?? 'Some'} hours overflow. ${capacity.recommendations?.[0] ?? 'Reduce load or extend availability.'}`
                  : `${ghostBlocks.length} suggestions ready. Calendar is ${calendarConnected ? 'connected' : 'not connected'}.`}
              </div>
              <div className="mt-1 break-words text-cyan-100/80">{solverStatus}</div>
            </div>
          </div>
        </div>
      </section>

      <div className="grid shrink-0 grid-cols-[auto_1fr] items-center gap-2 sm:flex sm:flex-wrap sm:justify-between sm:gap-3">
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 sm:h-10 sm:w-10"
            onClick={() => setCurrentDate(addDays(currentDate, -7))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-9 px-3 text-xs sm:h-10 sm:px-4 sm:text-sm"
            onClick={() => setCurrentDate(new Date())}
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 sm:h-10 sm:w-10"
            onClick={() => setCurrentDate(addDays(currentDate, 7))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center justify-self-end rounded-md border bg-background/80 p-0.5 sm:order-3 sm:gap-1 sm:p-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Zoom calendar out"
            className="h-8 w-8 sm:h-10 sm:w-10"
            onClick={() => setSlotHeight(value => Math.max(36, value - 12))}
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="w-11 text-center text-[11px] text-muted-foreground sm:w-14 sm:text-xs">{Math.round((slotHeight / 60) * 100)}%</span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Zoom calendar in"
            className="h-8 w-8 sm:h-10 sm:w-10"
            onClick={() => setSlotHeight(value => Math.min(132, value + 12))}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="col-span-2 min-w-0 text-[11px] leading-tight text-muted-foreground sm:order-2 sm:col-auto sm:text-sm">
          <span className="sm:hidden">Tap to edit. Drag to move. Pinch to zoom.</span>
          <span className="hidden sm:inline">Tap to edit. Hold and drag to move. Pinch the timeline to zoom.</span>
        </div>
      </div>

      {needsDetails.length > 0 && (
        <section className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 shadow-sm dark:border-amber-500/30 dark:bg-amber-950/25">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-amber-700 dark:text-amber-200" />
              <div>
                <h2 className="text-sm font-semibold text-amber-950 dark:text-amber-50">
                  {needsDetails.length} incomplete activit{needsDetails.length === 1 ? 'y' : 'ies'} held from Solve
                </h2>
                <p className="text-xs text-amber-800/80 dark:text-amber-100/75">
                  Fill required fields in Unscheduled before they enter the calendar.
                </p>
              </div>
            </div>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-amber-300 bg-white text-amber-950 hover:bg-amber-100 dark:border-amber-500/40 dark:bg-slate-950 dark:text-amber-50 dark:hover:bg-amber-950/40"
            >
              <NavLink to="/unscheduled">Open Unscheduled</NavLink>
            </Button>
          </div>
        </section>
      )}

      <div
        className="native-scroll temporal-grid relative min-h-[min(36dvh,320px)] flex-1 overflow-auto rounded-lg border bg-background/82 shadow-sm sm:min-h-[420px]"
        {...pinchHandlers}
      >
        {!hasCalendarContent && (
          <CalendarEmptyState onRefresh={() => void refreshScheduler()} isRefreshing={isRefreshing} />
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="calendar-week-canvas min-w-[760px] sm:min-w-[980px]">
            <div className="sticky top-0 z-20 grid grid-cols-[56px_repeat(7,minmax(100px,1fr))] border-b bg-background/95 backdrop-blur sm:grid-cols-[72px_repeat(7,minmax(120px,1fr))]">
              <div className="border-r p-2 text-xs text-muted-foreground">PST</div>
              {days.map(day => (
                <div key={day.toISOString()} className="border-r p-2 sm:p-3">
                  <div className="text-xs uppercase text-muted-foreground">{format(day, 'EEE')}</div>
                  <div className={cn('text-lg font-semibold', toLocalDateString(day) === toLocalDateString(new Date()) && 'text-cyan-600')}>
                    {format(day, 'd')}
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-[56px_repeat(7,minmax(100px,1fr))] sm:grid-cols-[72px_repeat(7,minmax(120px,1fr))]">
              {HOURS.map(hour => (
                <HourRow
                  key={hour}
                  hour={hour}
                  days={days}
                  blocksBySlot={blocksBySlot}
                  taskById={taskById}
                  calendarEventById={calendarEventById}
                  slotHeight={slotHeight}
                  workingWindow={workingWindow}
                  onConfirmGhost={confirmGhostBlock}
                  onEditCalendarEvent={setEditingCalendarEvent}
                  onEditTask={setEditingTask}
                />
              ))}
            </div>
          </div>
        </DndContext>
      </div>

      <CalendarCommitmentEditor
        event={editingCalendarEvent}
        open={Boolean(editingCalendarEvent)}
        onClose={() => setEditingCalendarEvent(null)}
        onSave={handleCalendarEventSaved}
      />
      <TaskEditor
        task={editingTask}
        open={Boolean(editingTask)}
        onClose={() => setEditingTask(null)}
        onSave={handleSaveTask}
        onDelete={handleDeleteTask}
        onRescheduleTomorrow={handleRescheduleTomorrow}
      />
    </div>
  );
}

function HourRow({
  hour,
  days,
  blocksBySlot,
  taskById,
  calendarEventById,
  slotHeight,
  workingWindow,
  onConfirmGhost,
  onEditCalendarEvent,
  onEditTask,
}: {
  hour: number;
  days: Date[];
  blocksBySlot: Map<string, ScheduleBlock[]>;
  taskById: Map<string, TaskUI>;
  calendarEventById: Map<string, CalendarEvent>;
  slotHeight: number;
  workingWindow: WorkingWindow;
  onConfirmGhost: (id: string) => void;
  onEditCalendarEvent: (event: CalendarEvent) => void;
  onEditTask: (task: TaskUI) => void;
}) {
  const label =
    hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;
  const boundaryMinutes = getWorkingBoundaryMinutes(hour, workingWindow);
  const outsideWorkingHours = isOutsideWorkingHour(hour, workingWindow);

  return (
    <>
      <div
        className={cn(
          'border-r border-t bg-muted/20 p-2 text-right text-xs text-muted-foreground',
          boundaryMinutes.length > 0 && 'border-t-2 border-t-foreground/80'
        )}
      >
        {label}
      </div>
      {days.map(day => {
        const dateKey = toLocalDateString(day);
        const hourBlocks = blocksBySlot.get(`${dateKey}-${hour}`) || [];

        return (
          <DroppableTimeSlot
            key={`${dateKey}-${hour}`}
            id={`week-${dateKey}-${hour}`}
            dateKey={dateKey}
            hour={hour}
            slotHeight={slotHeight}
            workingBoundaryMinutes={boundaryMinutes}
            isOutsideWorkingHours={outsideWorkingHours}
          >
            {hourBlocks.map(block => (
              <ScheduledTaskBlock
                key={block.id}
                block={block}
                task={taskById.get(block.taskId)}
                onClick={() => {
                  if (block.isGhost) {
                    void onConfirmGhost(block.id);
                    return;
                  }
                  if (block.isCalendarEvent) {
                    const event = calendarEventById.get(block.taskId);
                    if (event) onEditCalendarEvent(event);
                    return;
                  }
                  const task = taskById.get(block.taskId);
                  if (task) onEditTask(task);
                }}
              />
            ))}
          </DroppableTimeSlot>
        );
      })}
    </>
  );
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'cyan' | 'amber' | 'green' | 'rose';
}) {
  const toneClass = {
    cyan: 'text-cyan-100 border-cyan-300/20 bg-cyan-300/10',
    amber: 'text-amber-100 border-amber-300/20 bg-amber-300/10',
    green: 'text-emerald-100 border-emerald-300/20 bg-emerald-300/10',
    rose: 'text-rose-100 border-rose-300/20 bg-rose-300/10',
  }[tone];

  return (
    <div className={cn('min-w-0 rounded-lg border p-1.5 sm:p-3', toneClass)}>
      <div className="break-words text-[9px] uppercase leading-tight text-white/55 sm:text-xs">{label}</div>
      <div className="mt-1 text-base font-semibold leading-none sm:text-2xl">{value}</div>
    </div>
  );
}

function getMissingPlanningFields(task: TaskUI): string[] {
  const missing: string[] = [];
  if (!String(task.title || '').trim()) missing.push('title');
  if (!task.bundleId) missing.push('bundle');
  if (!Number.isFinite(Number(task.estimatedDuration)) || Number(task.estimatedDuration) <= 0) {
    missing.push('duration');
  }

  const profile = task.stateProfile;
  const requiredScores: Array<[keyof NonNullable<TaskUI['stateProfile']>, string]> = [
    ['required_productivity_energy', 'required P'],
    ['required_rejuvenation_energy', 'required RJ'],
    ['productivity_return', 'P return'],
    ['rejuvenation_return', 'RJ return'],
  ];
  for (const [key, label] of requiredScores) {
    const value = profile?.[key];
    if (value === null || value === undefined || !Number.isFinite(Number(value))) {
      missing.push(label);
    }
  }

  return Array.from(new Set(missing));
}

type WorkingWindow = {
  startMinutes: number;
  endMinutes: number;
};

function parseWorkingWindow(workHours?: { start?: string; end?: string } | null): WorkingWindow {
  return {
    startMinutes: parseClockMinutes(workHours?.start, 9 * 60),
    endMinutes: parseClockMinutes(workHours?.end, 17 * 60),
  };
}

function parseClockMinutes(value: string | undefined, fallback: number): number {
  if (!value) return fallback;
  const [hourText, minuteText = '0'] = value.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return fallback;
  return Math.max(0, Math.min(24 * 60, hour * 60 + minute));
}

function getWorkingBoundaryMinutes(hour: number, window: WorkingWindow): number[] {
  const hourStart = hour * 60;
  const hourEnd = hourStart + 60;
  return [window.startMinutes, window.endMinutes]
    .filter(minutes => minutes >= hourStart && minutes < hourEnd)
    .map(minutes => minutes - hourStart);
}

function isOutsideWorkingHour(hour: number, window: WorkingWindow): boolean {
  const hourStart = hour * 60;
  const hourEnd = hourStart + 60;
  return hourEnd <= window.startMinutes || hourStart >= window.endMinutes;
}

function normalizeScheduleBlocks(data: any, taskIds?: Set<string>) {
  const ghostBlocks = (data.ghost_blocks || [])
    .filter((block: any) => !taskIds || taskIds.has(block.task_id))
    .map((block: any) => ({
      id: block.id,
      taskId: block.task_id,
      title: block.title || 'Suggested activity',
      startTime: block.proposed_start,
      endTime: block.proposed_end,
      isGhost: true,
    }));

  const workSegments = (data.work_segments || []).map((segment: any) => ({
    id: segment.id,
    taskId: segment.task_id,
    title: segment.segment_type === 'pause' ? 'Free time' : segment.title || 'Focused work',
    startTime: segment.start_time,
    endTime: segment.end_time,
    isGhost: false,
    priority: segment.segment_type === 'pause' ? 5 : 3,
  }));

  return [...ghostBlocks, ...workSegments];
}

function normalizeGhostBlocks(data: any, taskIds?: Set<string>) {
  return (data.ghost_blocks || [])
    .filter((block: any) => !taskIds || taskIds.has(block.task_id))
    .map((block: any) => ({
      id: block.id,
      taskId: block.task_id,
      proposedStart: block.proposed_start,
      proposedEnd: block.proposed_end,
      cost: block.cost,
      confidence: block.confidence,
      reason: block.reason,
    }));
}

function normalizeCalendarEvents(events: any[] = []) {
  return events
    .map(event => ({
      id: event.id,
      title: event.title || event.summary || 'Calendar event',
      start: event.start || event.start_time,
      end: event.end || event.end_time,
      isImmovable: Boolean(event.is_immovable ?? true),
      isFlexHold: Boolean(event.is_flex_hold),
      stateProfile: event.state_profile || null,
      customAttributes: parseCustomAttributes(event.custom_attributes),
    }))
    .filter(isRenderableCalendarEvent);
}

function isRenderableCalendarEvent(event: CalendarEvent): boolean {
  if (isAllDayCalendarEvent(event)) return false;
  if (event.isFlexHold) return true;

  const attributes = event.customAttributes || {};
  const source = String(attributes.source || '').toLowerCase();
  return source !== 'canvas' && source !== 'book-tracker' && !attributes.canvas && !attributes.book_tracker;
}

function isAllDayCalendarEvent(event: { start?: string; end?: string }): boolean {
  return !String(event.start || '').includes('T') || !String(event.end || '').includes('T');
}

function parseCustomAttributes(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
